import React, { Fragment, useRef, useState, useCallback } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';

/**
 * SelectMenu — styled, accessible single-choice picker (LAT-13).
 *
 * Replaces every native <select> in the app. Built on Headless UI's Listbox
 * for full keyboard nav, screen reader, and outside-click handling.
 *
 * Two option shapes:
 *   - Flat:    [{ value, label, disabled? }, …]
 *   - Grouped: [{ group: 'Crew', options: [{value,label}, …] }, …]
 *
 * Caret has right padding by default (LAT-13).
 *
 * Usage:
 *   <SelectMenu
 *     value={status}
 *     onChange={setStatus}
 *     placeholder="Select status…"
 *     options={[
 *       { value: 'lead',   label: 'Lead' },
 *       { value: 'active', label: 'Active' },
 *     ]}
 *   />
 *
 *   <SelectMenu
 *     value={crewId}
 *     onChange={setCrewId}
 *     options={[
 *       { group: 'Crews', options: [{value:'c1',label:'Crew Alpha'}] },
 *       { group: 'Individual techs', options: [{value:'t1',label:'Sam'}] },
 *     ]}
 *   />
 */
export default function SelectMenu({
  value,
  onChange,
  options = [],
  placeholder = 'Select…',
  disabled = false,
  invalid = false,
  size = 'md',     // 'sm' | 'md' | 'lg'
  width = 'auto',  // 'auto' | 'full' | css length
  id,
  name,
  className,
  buttonClassName,
  'aria-label': ariaLabel,
  renderValue,     // (option) => ReactNode  (custom selected display)
  renderOption,    // (option, {selected, active}) => ReactNode
}) {
  const flatOptions = React.useMemo(() => flatten(options), [options]);
  const selected = flatOptions.find((o) => o.value === value);

  // §3: flip-up placement. Measure on open; if below-space < 288px (max-h-72)
  // the panel opens upward. Measured against the viewport.
  const containerRef = useRef(null);
  const [flipUp, setFlipUp] = useState(false);
  const checkFlip = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    setFlipUp(spaceBelow < 288);
  }, []);

  const heightVar =
    size === 'sm' ? '28px' : size === 'lg' ? '40px' : 'var(--input-h)';

  const widthStyle =
    width === 'full' ? '100%' : width === 'auto' ? 'auto' : width;

  return (
    <Listbox value={value ?? null} onChange={onChange} disabled={disabled} name={name}>
      <div
        ref={containerRef}
        data-portica-role="select"
        className={clsx('relative', className)}
        style={{ width: widthStyle }}
        onClick={checkFlip}
      >
        <Listbox.Button
          id={id}
          aria-label={ariaLabel}
          className={clsx(
            'relative w-full text-left text-sm',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-action-primary)]',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            'transition-colors',
            buttonClassName
          )}
          style={{
            height: heightVar,
            paddingLeft: '0.75rem',
            paddingRight: '2.25rem',  // room for caret
            borderRadius: 'var(--radius-md)',
            border: `1px solid ${
              invalid ? 'var(--color-error)' : 'var(--color-border)'
            }`,
            backgroundColor: 'var(--color-surface)',
            color: selected
              ? 'var(--color-text-default)'
              : 'var(--color-text-muted)',
          }}
        >
          <span
            className="block truncate"
            title={selected?.label || undefined}
          >
            {selected
              ? (renderValue ? renderValue(selected) : selected.label)
              : placeholder}
          </span>
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronUpDownIcon
              className="h-4 w-4"
              style={{ color: 'var(--color-text-muted)' }}
              aria-hidden="true"
            />
          </span>
        </Listbox.Button>

        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Listbox.Options
            className={clsx(
              'absolute z-40 max-h-72 w-full overflow-auto py-1 text-sm focus:outline-none',
              flipUp ? 'bottom-full mb-1' : 'mt-1'
            )}
            style={{
              borderRadius: 'var(--radius-md)',
              backgroundColor: 'var(--color-surface)',
              border: '1px solid var(--color-border-subtle)',
              boxShadow: 'var(--card-shadow)',
              minWidth: '10rem',
            }}
          >
            {options.length === 0 && (
              <div
                className="px-3 py-2 text-sm"
                style={{ color: 'var(--color-text-muted)' }}
              >
                No options
              </div>
            )}
            {isGrouped(options)
              ? options.map((group, gi) => (
                  <div key={group.group || gi}>
                    <div
                      className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wider"
                      style={{ color: 'var(--color-text-muted)' }}
                    >
                      {group.group}
                    </div>
                    {group.options.map((opt) => (
                      <OptionItem
                        key={opt.value}
                        option={opt}
                        renderOption={renderOption}
                      />
                    ))}
                  </div>
                ))
              : options.map((opt) => (
                  <OptionItem
                    key={opt.value}
                    option={opt}
                    renderOption={renderOption}
                  />
                ))}
          </Listbox.Options>
        </Transition>
      </div>
    </Listbox>
  );
}

function OptionItem({ option, renderOption }) {
  return (
    <Listbox.Option
      key={option.value}
      value={option.value}
      disabled={option.disabled}
      className={({ active, disabled }) =>
        clsx(
          'relative cursor-pointer select-none py-2 pl-8 pr-3',
          active && 'bg-[color:var(--neutral-50)]',
          disabled && 'opacity-50 cursor-not-allowed'
        )
      }
      style={{
        color: 'var(--color-text-default)',
      }}
    >
      {({ selected, active }) => (
        <>
          {selected && (
            <span
              className="absolute inset-y-0 left-2 flex items-center"
              style={{ color: 'var(--color-action-primary)' }}
            >
              <CheckIcon className="h-4 w-4" aria-hidden="true" />
            </span>
          )}
          <span
            className={clsx('block truncate', selected && 'font-semibold')}
            title={option.label}
          >
            {renderOption ? renderOption(option, { selected, active }) : option.label}
          </span>
          {option.hint && (
            <span
              className="block text-xs"
              style={{ color: 'var(--color-text-muted)' }}
            >
              {option.hint}
            </span>
          )}
        </>
      )}
    </Listbox.Option>
  );
}

function isGrouped(options) {
  return options.length > 0 && options[0] && 'group' in options[0] && Array.isArray(options[0].options);
}

function flatten(options) {
  if (!Array.isArray(options)) return [];
  if (isGrouped(options)) {
    return options.flatMap((g) => g.options || []);
  }
  return options;
}
